import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { MutationSaveStatus } from "./MutationSaveStatus";
import { CyItem } from "./CyItem";

interface Props {
  htmlTagIdAndName: string;
  status: MutationSaveStatus;
}

/**
 * Small inline icon communicating whether a field's auto-save mutation is in
 * flight, has just completed, or failed.
 */
export function SaveStatusIndicator({ htmlTagIdAndName, status }: Props) {
  const { t } = useTranslation();

  if (status === "idle") {
    return null;
  }

  const iconByStatus: Record<
    Exclude<MutationSaveStatus, "idle">,
    JSX.Element
  > = {
    saving: (
      <FontAwesomeIcon
        icon={faSpinner}
        spin
        className="text-secondary"
        title={t("items.saveStatus.saving")}
      />
    ),
    saved: (
      <FontAwesomeIcon
        icon={faCheck}
        className="text-success"
        title={t("items.saveStatus.saved")}
      />
    ),
    error: (
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        className="text-danger"
        title={t("items.saveStatus.error")}
      />
    ),
  };

  return (
    <span
      className="ms-2"
      data-cy={CyItem.FieldSaveStatus}
      data-cy-key={htmlTagIdAndName}
      data-save-status={status}
      aria-live="polite"
      aria-label={t(`items.saveStatus.${status}`)}
    >
      {iconByStatus[status]}
    </span>
  );
}
